import {
    type BimLevel,
    type BimPlan,
    type DrawingGroupItem,
    getBimPlans,
    type ModelMapping,
} from "apis/procore/bimPlans";
import { getLocations } from "apis/procore/locations";
import { AsyncStatus } from "types/misc";
import { log } from "utils/log";

import type { MiniMapPayload } from "./minimapSlice";
import type { Level } from "./types";

const l = log.miniMap.utils;

export type FetchMiniMapDataArgs = {
    companyId: number;
    projectId: number;
    modelRevisionId: string;
};

const emptyPayload = (): MiniMapPayload => ({
    levels: [],
    drawings: {},
    modelABs: {},
    drawingABs: {},
    currentLevelId: null,
    currentDrawingId: null,
});

type NormalizedDrawingMapping = {
    id: string;
    bim_file_revision_instance_id: string;
    point_a: { x: number; y: number };
    point_b: { x: number; y: number };
    sheet_dimensions: { width: number; height: number };
    use_for_2d_nav: boolean;
};

type MappingGroup = {
    level: BimLevel | null;
    drawingMappings: NormalizedDrawingMapping[];
    drawingGroupItems: DrawingGroupItem[];
    modelMapping: ModelMapping | null;
};

function getPlanMappingGroups(plan: BimPlan): MappingGroup[] {
    const drawingGroupItems = plan.drawing_group?.drawing_group_items ?? [];

    if (plan.sheet_mappings && plan.sheet_mappings.length > 0) {
        const itemByInstanceId = new Map(drawingGroupItems.map((item) => [item.bim_file_revision_instance_id, item]));

        return plan.sheet_mappings.map((sheet): MappingGroup => {
            const dims = sheet.sheet_mapping?.sheet_dimensions;
            const drawingMappings: NormalizedDrawingMapping[] =
                sheet.sheet_mapping && dims
                    ? [
                          {
                              id: sheet.id,
                              bim_file_revision_instance_id: sheet.bim_file_revision_instance_id,
                              point_a: sheet.sheet_mapping.point_a,
                              point_b: sheet.sheet_mapping.point_b,
                              sheet_dimensions: dims,
                              use_for_2d_nav: sheet.use_for_2d_nav ?? false,
                          },
                      ]
                    : [];

            const modelMapping: ModelMapping | null = sheet.model_mapping
                ? {
                      mapping: {
                          id: `${sheet.id}-model`,
                          point_a: sheet.model_mapping.point_a,
                          point_b: sheet.model_mapping.point_b,
                      },
                  }
                : null;

            const drawingItem = itemByInstanceId.get(sheet.bim_file_revision_instance_id);

            return {
                level: sheet.bim_level ?? null,
                drawingMappings,
                drawingGroupItems: drawingItem ? [drawingItem] : [],
                modelMapping,
            };
        });
    }

    const drawingMappings: NormalizedDrawingMapping[] = (plan.drawing_mappings ?? []).flatMap((m) =>
        m.sheet_dimensions
            ? [
                  {
                      id: m.id,
                      bim_file_revision_instance_id: m.bim_file_revision_instance_id,
                      point_a: m.point_a,
                      point_b: m.point_b,
                      sheet_dimensions: m.sheet_dimensions,
                      use_for_2d_nav: m.use_for_2d_nav ?? false,
                  },
              ]
            : [],
    );

    return [
        {
            level: plan.bim_level ?? null,
            drawingMappings,
            drawingGroupItems,
            modelMapping: plan.model_mapping ?? null,
        },
    ];
}

/**
 * Fetch and compose the mini map payload (levels, drawings, drawing/model
 * mappings) by chaining the Procore BIM endpoints (model revision → plans →
 * locations).
 *
 * Pure data function: no Redux dispatch, no React, no per-drawing image
 * fetching. Consumed by the `getMiniMapData` RTK Query endpoint on
 * `procoreApi`, which is responsible for resolving `modelRevisionId` (using
 * the `getModelRevisions` endpoint with `latest: true`). The slice picks up
 * the result via an extraReducers matcher on `getMiniMapData.matchFulfilled`.
 * Per-drawing image prefetch is handled by `useMiniMapData`.
 *
 * Filters drawings to only those with `use_for_2d_nav === true`.
 */
export async function fetchMiniMapData(args: FetchMiniMapDataArgs): Promise<MiniMapPayload> {
    const { companyId, projectId, modelRevisionId } = args;

    const plans = await getBimPlans({ companyId, projectId, modelRevisionId });
    l.debug("Fetched plans for mini map:", plans);

    const planGroups = plans.map((plan) => getPlanMappingGroups(plan));
    const locationIds = Array.from(
        new Set(planGroups.flat().flatMap((group) => (group.level?.location_id ? [group.level.location_id] : []))),
    );

    const locations = await getLocations({ companyId, projectId, locationIds });
    l.debug("Fetched locations for mini map:", locations);

    const data: MiniMapPayload = emptyPayload();
    const bimFileRevisionInstanceIdToDrawingABId: Record<string, string> = {};

    planGroups.flat().forEach((group) => {
        // Only sheets explicitly enabled for 2D navigation should appear in
        // the minimap. The "Use for 2D Navigation" / "Do not use" actions
        // flip `use_for_2d_nav` on the mapping and trigger a refetch of
        // plans/extended.
        const enabledNavInstanceIds = new Set<string>();
        group.drawingMappings.forEach((mapping) => {
            if (!mapping.use_for_2d_nav) {
                return;
            }
            enabledNavInstanceIds.add(mapping.bim_file_revision_instance_id);
            data.drawingABs[mapping.id] = {
                id: mapping.id,
                pointA: [mapping.point_a.x, mapping.point_a.y],
                pointB: [mapping.point_b.x, mapping.point_b.y],
                sheetDimensions: [mapping.sheet_dimensions.width, mapping.sheet_dimensions.height],
            };
            bimFileRevisionInstanceIdToDrawingABId[mapping.bim_file_revision_instance_id] = mapping.id;
        });

        const navItems = group.drawingGroupItems.filter((item) =>
            enabledNavInstanceIds.has(item.bim_file_revision_instance_id),
        );

        if (group.level && navItems.length > 0) {
            const level = group.level;
            const existing = data.levels.find((lvl) => lvl.id === level.id);
            if (existing) {
                existing.drawingIds.push(...navItems.map((item) => item.id));
            } else {
                const defaultName = "Unknown Level";
                const location = locations.find((loc) => loc.id === level.location_id);
                data.levels.push({
                    id: level.id,
                    elevation: level.elevation,
                    name: location?.name || defaultName,
                    nodeName: location?.node_name || defaultName,
                    drawingIds: navItems.map((item) => item.id),
                } satisfies Level);
            }
        }

        const modelAB = group.modelMapping?.mapping;
        if (!modelAB) {
            return;
        }

        data.modelABs[modelAB.id] = {
            id: modelAB.id,
            pointA: [modelAB.point_a.x, modelAB.point_a.y, modelAB.point_a.z],
            pointB: [modelAB.point_b.x, modelAB.point_b.y, modelAB.point_b.z],
        };

        navItems.forEach((drawing) => {
            const drawingABId = bimFileRevisionInstanceIdToDrawingABId[drawing.bim_file_revision_instance_id];
            if (!drawingABId) {
                return;
            }
            data.drawings[drawing.id] = {
                id: drawing.id,
                name: drawing.file_info.bim_file.file_name,
                drawingData: {
                    url: drawing.file_info.bim_file_revision.download_url,
                    previewAsyncState: { status: AsyncStatus.Initial },
                    asyncState: { status: AsyncStatus.Initial },
                },
                drawingABId,
                modelABId: modelAB.id,
            };
        });
    });

    // Seed the selection with the lowest level's first sheet. Sorting first
    // makes this deterministic rather than "whichever plan the API returned
    // first", which on a multi-building project could be an unrelated model.
    // It is only a seed: the slice keeps an existing selection across refetches,
    // and the camera handlers immediately re-resolve it against the camera's
    // actual position via `findDrawingIdForCamera`.
    data.levels.sort((a, b) => a.elevation - b.elevation);
    data.currentLevelId = data.levels.length > 0 ? data.levels[0].id : null;
    if (data.currentLevelId) {
        const drawingIds = data.levels.find((lvl) => lvl.id === data.currentLevelId)?.drawingIds;
        if (drawingIds && drawingIds.length > 0) {
            data.currentDrawingId = drawingIds[0];
        }
    }

    return data;
}
